/* ============================================================
   Chesto — Social carousel
   ------------------------------------------------------------
   Vanilla custom element. Drop-in for Shopify Dawn.
   One instance per <chesto-social-carousel> tag.

   Behaviours:
   - drag-to-scroll (pointer events, mouse + touch + pen)
   - autoplay (optional, pauses on hover/focus)
   - prev/next buttons + page dots
   - keyboard ← → when section has focus
   - exact card width per `visible-count` (desktop) or peek (mobile)
   - "first card" click is suppressed if the user actually dragged
   ============================================================ */
(function () {
  if (window.customElements && customElements.get('chesto-social-carousel')) return;

  class ChestoSocialCarousel extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('[data-csc-viewport]');
      this.track    = this.querySelector('[data-csc-track]');
      this.dotsWrap = this.querySelector('[data-csc-dots]');
      this.prevBtn  = this.querySelector('[data-csc-prev]');
      this.nextBtn  = this.querySelector('[data-csc-next]');

      if (!this.viewport || !this.track) return;

      this.visibleCount = parseInt(this.getAttribute('visible-count'), 10) || 5;
      this.autoplay     = this.getAttribute('autoplay') === 'true';
      this.autoplaySpeed = Math.max(2, parseInt(this.getAttribute('autoplay-speed'), 10) || 4);

      this._pageIndex = 0;
      this._pageCount = 1;
      this._autoTimer = null;

      this._bindNav();
      this._bindDrag();
      this._bindKeyboard();
      this._bindResize();
      this._sizeCards();
      this._hydrateThumbnails();
      this._wireReveal();
      this._recomputePages();
      this._renderDots();

      if (this.autoplay) this._startAutoplay();

      // Pause autoplay when user interacts
      this.addEventListener('pointerenter', () => this._pauseAutoplay());
      this.addEventListener('pointerleave', () => this.autoplay && this._startAutoplay());
      this.addEventListener('focusin',      () => this._pauseAutoplay());
      this.addEventListener('focusout',     () => this.autoplay && this._startAutoplay());
    }

    disconnectedCallback() {
      this._pauseAutoplay();
      if (this._ro) this._ro.disconnect();
    }

    /* ---------- nav ---------- */
    _bindNav() {
      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this._goPrev());
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this._goNext());
      this.viewport.addEventListener('scroll', () => this._recomputePages(), { passive: true });
    }

    _cardStep() {
      const card = this.track.firstElementChild;
      if (!card) return this.viewport.clientWidth;
      const gap = parseInt(getComputedStyle(this.track).gap, 10) || 0;
      return card.getBoundingClientRect().width + gap;
    }

    _goPrev() {
      this.viewport.scrollBy({ left: -this._cardStep(), behavior: 'smooth' });
    }

    _goNext() {
      const max = this.viewport.scrollWidth - this.viewport.clientWidth;
      if (this.viewport.scrollLeft >= max - 2) {
        this.viewport.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        this.viewport.scrollBy({ left: this._cardStep(), behavior: 'smooth' });
      }
    }

    _goToPage(i) {
      this.viewport.scrollTo({ left: i * this.viewport.clientWidth, behavior: 'smooth' });
    }

    /* ---------- pages / dots ---------- */
    _recomputePages() {
      const vw = this.viewport.clientWidth;
      if (vw <= 0) { this._setPageCount(1); this._setPageIndex(0); return; }
      const max = Math.max(0, this.track.scrollWidth - vw);
      const pages = Math.max(1, Math.ceil(max / vw) + 1);
      this._setPageCount(pages);
      const idx = Math.round(this.viewport.scrollLeft / vw);
      this._setPageIndex(Math.min(pages - 1, idx));

      if (this.prevBtn) this.prevBtn.disabled = this.viewport.scrollLeft <= 1;
    }

    _setPageCount(n) {
      if (n === this._pageCount) return;
      this._pageCount = n;
      this._renderDots();
    }

    _setPageIndex(i) {
      if (i === this._pageIndex) return;
      this._pageIndex = i;
      if (!this.dotsWrap) return;
      const dots = this.dotsWrap.querySelectorAll('.csc__dot');
      dots.forEach((d, k) => d.classList.toggle('is-active', k === i));
    }

    _renderDots() {
      if (!this.dotsWrap) return;
      this.dotsWrap.innerHTML = '';
      for (let i = 0; i < this._pageCount; i++) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'csc__dot' + (i === this._pageIndex ? ' is-active' : '');
        b.setAttribute('aria-label', 'Ga naar pagina ' + (i + 1));
        b.addEventListener('click', () => this._goToPage(i));
        this.dotsWrap.appendChild(b);
      }
    }

    /* ---------- drag ---------- */
    _bindDrag() {
      const vp = this.viewport;
      let isDown = false, isTouch = false, startX = 0, startScroll = 0, moved = false;

      // Threshold in px voordat we het als een drag (en niet een tap) beschouwen.
      // Touch vingers jitter meer dan een muis — hogere threshold voorkomt
      // dat normale taps als drag worden onderschept.
      const DRAG_THRESHOLD_MOUSE = 4;
      const DRAG_THRESHOLD_TOUCH = 14;

      vp.addEventListener('pointerdown', (e) => {
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        isDown = true; moved = false;
        isTouch = e.pointerType !== 'mouse';
        startX = e.clientX;
        startScroll = vp.scrollLeft;
        // 'is-dragging' niet meer direct toevoegen — dat zet pointer-events: none
        // op cards en blokkeert taps op mobiel. We voegen het pas toe ná de
        // threshold-overschrijding (zie pointermove).
        if (!isTouch) {
          try { vp.setPointerCapture(e.pointerId); } catch (_) {}
        }
        this._pauseAutoplay();
      });

      vp.addEventListener('pointermove', (e) => {
        if (!isDown) return;
        const dx = e.clientX - startX;
        const threshold = isTouch ? DRAG_THRESHOLD_TOUCH : DRAG_THRESHOLD_MOUSE;
        if (Math.abs(dx) > threshold) {
          if (!moved) {
            moved = true;
            vp.classList.add('is-dragging');
          }
        }
        // Alleen handmatig scrollen op MUIS. Op touch zorgt het OS al voor
        // momentum-scroll via overflow-x:auto + -webkit-overflow-scrolling.
        if (!isTouch && moved) {
          vp.scrollLeft = startScroll - dx;
        }
      });

      const end = (e) => {
        if (!isDown) return;
        isDown = false;
        vp.classList.remove('is-dragging');
        try { vp.releasePointerCapture(e.pointerId); } catch (_) {}
        if (moved) {
          // Onderschep alleen de eerstvolgende click (anti-drag-navigation)
          const blockClick = (ev) => {
            ev.preventDefault();
            ev.stopPropagation();
            vp.removeEventListener('click', blockClick, true);
          };
          vp.addEventListener('click', blockClick, true);
          // Veiligheidsklep: als click niet binnen 300ms komt, alsnog cleanen
          setTimeout(() => vp.removeEventListener('click', blockClick, true), 300);
        }
        if (this.autoplay) this._startAutoplay();
      };
      vp.addEventListener('pointerup', end);
      vp.addEventListener('pointercancel', end);
      // pointerleave NIET op touch — die fired soms voortijdig op iOS
      vp.addEventListener('pointerleave', (e) => {
        if (e.pointerType !== 'mouse') return;
        end(e);
      });
    }

    /* ---------- keyboard ---------- */
    _bindKeyboard() {
      this.addEventListener('keydown', (e) => {
        if (e.target && /input|textarea|select/i.test(e.target.tagName)) return;
        if (e.key === 'ArrowLeft')  { e.preventDefault(); this._goPrev(); }
        if (e.key === 'ArrowRight') { e.preventDefault(); this._goNext(); }
      });
    }

    /* ---------- sizing ---------- */
    _bindResize() {
      this._ro = new ResizeObserver(() => {
        this._sizeCards();
        this._recomputePages();
      });
      this._ro.observe(this.viewport);
    }

    _sizeCards() {
      const w = this.viewport.clientWidth;
      if (w <= 0) return;
      const gap = parseInt(getComputedStyle(this.track).gap, 10) || 0;
      const isMobile = window.matchMedia('(max-width: 749px)').matches;
      let cardW;
      if (isMobile) {
        cardW = Math.max(120, Math.floor(w * 0.66));
      } else {
        const n = Math.max(1, this.visibleCount);
        cardW = Math.max(120, Math.floor((w - gap * (n - 1)) / n));
      }
      this.track.style.setProperty('--csc-card-w', cardW + 'px');
    }

    /* ---------- entry reveal — header + viewport ---------- */
    _wireReveal() {
      const els = this.querySelectorAll('[data-csc-reveal]');
      if (!els.length) return;
      if (!('IntersectionObserver' in window)) {
        els.forEach((el) => el.classList.add('is-in'));
        return;
      }
      const io = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('is-in');
            io.unobserve(e.target);
          }
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
      els.forEach((el) => io.observe(el));
      // Safety: na 1.5s alsnog tonen
      setTimeout(() => els.forEach((el) => el.classList.add('is-in')), 1500);
    }

    /* ---------- thumbnail hydration ---------- */
    /* Voor elke card zonder geüploade afbeelding: probeer de thumbnail
       automatisch op te halen uit de URL. YouTube wordt direct herkend;
       Instagram/TikTok/Facebook/anders gaan via Microlink (gratis CORS
       API met og:image). Resultaat 30 dagen gecached in localStorage. */
    _hydrateThumbnails() {
      const cards = this.querySelectorAll('.csc__card[data-csc-href]');
      cards.forEach((card) => {
        const url = card.getAttribute('data-csc-href');
        const inner = card.querySelector('.csc__media-inner');
        if (!url || !inner) return;
        // Skip als er al een geüploade afbeelding staat
        if (inner.querySelector('img')) return;
        ChestoSocialThumb.fetch(url).then((src) => {
          if (!src || !this.isConnected) return;
          const img = new Image();
          img.loading = 'lazy';
          img.decoding = 'async';
          img.alt = card.getAttribute('aria-label') || '';
          img.src = src;
          img.addEventListener('load', () => {
            inner.appendChild(img);
            card.setAttribute('data-csc-hydrated', 'true');
          });
          img.addEventListener('error', () => {/* houd gradient */});
        });
      });
    }

    /* ---------- autoplay ---------- */
    _startAutoplay() {
      this._pauseAutoplay();
      this._autoTimer = setInterval(() => this._goNext(), this.autoplaySpeed * 1000);
    }
    _pauseAutoplay() {
      if (this._autoTimer) { clearInterval(this._autoTimer); this._autoTimer = null; }
    }
  }

  customElements.define('chesto-social-carousel', ChestoSocialCarousel);

  /* ============================================================
     ChestoSocialThumb — auto-thumbnail fetcher
     ------------------------------------------------------------
     Stappen per URL:
       1. Cache check (localStorage, 30 dagen TTL)
       2. YouTube pattern → img.youtube.com (direct, geen API)
       3. Vimeo pattern → vimeo.com/api/oembed.json (CORS-friendly)
       4. Anders → Microlink API (CORS-enabled og:image fetcher)
     Faalt het? Return null → carrousel valt terug op gradient.
     ============================================================ */
  const ChestoSocialThumb = {
    CACHE_KEY: 'csc:thumb:',
    CACHE_TTL: 30 * 24 * 60 * 60 * 1000, // 30 dagen
    _inflight: new Map(),

    _readCache(url) {
      try {
        const raw = localStorage.getItem(this.CACHE_KEY + url);
        if (!raw) return null;
        const obj = JSON.parse(raw);
        if (!obj || !obj.t || Date.now() - obj.t > this.CACHE_TTL) return null;
        return obj.src || null;
      } catch (_) { return null; }
    },
    _writeCache(url, src) {
      try {
        localStorage.setItem(this.CACHE_KEY + url, JSON.stringify({ src, t: Date.now() }));
      } catch (_) {/* quota / private mode — geen drama */}
    },

    _youtubeId(url) {
      const m = url.match(/(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/|v\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/);
      return m ? m[1] : null;
    },
    _vimeoId(url) {
      const m = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
      return m ? m[1] : null;
    },

    async fetch(url) {
      if (!url || url === '#') return null;

      // Cache hit
      const cached = this._readCache(url);
      if (cached) return cached;

      // Dedupe in-flight requests voor dezelfde URL binnen één page-load
      if (this._inflight.has(url)) return this._inflight.get(url);

      const promise = this._doFetch(url).then((src) => {
        if (src) this._writeCache(url, src);
        this._inflight.delete(url);
        return src;
      });
      this._inflight.set(url, promise);
      return promise;
    },

    _isUsable(src) {
      // Microlink retourneert soms een base64 placeholder-PNG ('Login required'-grijs vlak)
      // voor sites die scrapers blokkeren (Instagram, FB, soms TikTok). Filter eruit.
      if (!src || typeof src !== 'string') return false;
      if (src.startsWith('data:')) return false;
      return true;
    },

    async _doFetch(url) {
      // YouTube fast-path — img.youtube.com is publiek, geen API/CORS issues
      const yt = this._youtubeId(url);
      if (yt) return `https://img.youtube.com/vi/${yt}/hqdefault.jpg`;

      // Vimeo fast-path — Vimeo oEmbed heeft CORS enabled
      const vi = this._vimeoId(url);
      if (vi) {
        try {
          const r = await fetch(`https://vimeo.com/api/oembed.json?url=https://vimeo.com/${vi}`);
          const j = await r.json();
          if (j && j.thumbnail_url && this._isUsable(j.thumbnail_url)) return j.thumbnail_url;
        } catch (_) {/* fallthrough */}
      }

      // Instagram / TikTok / Facebook / anders — via Microlink (gratis CORS proxy
      // die og:image probeert te schrapen). LET OP: Instagram en TikTok blokkeren
      // scrapers actief, dus deze platforms vallen vaak terug op de gradient.
      // Upload handmatig een poster voor 100% zekerheid.
      try {
        const r = await fetch('https://api.microlink.io/?url=' + encodeURIComponent(url));
        if (!r.ok) return null;
        const j = await r.json();
        const img = j && j.data && j.data.image;
        if (img && this._isUsable(img.url)) return img.url;
        const screen = j && j.data && j.data.screenshot;
        if (screen && this._isUsable(screen.url)) return screen.url;
        return null;
      } catch (_) {
        return null;
      }
    }
  };

  window.ChestoSocialThumb = ChestoSocialThumb;
})();
